/**
 * SessionDetailView: transcript + entity viewer for a single interview session.
 * Toggle between full Q&A transcript and entity summary grouped by domain.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { AIInterviewSession, AIInterviewMessage, LiveExtractedEntity } from '@ispot/shared'

import { aiInterviewApi } from '@/lib/api/client'
import { DOMAIN_COLORS } from '@/lib/constants/domainColors'
import { cn } from '@/lib/utils'

import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  ArrowLeft, MessageSquare, Clock, Sparkles,
  FileText, Layers, Loader2,
} from 'lucide-react'

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// ============================================================================
// SESSION DETAIL
// ============================================================================

interface SessionDetailViewProps {
  accountId: string
  projectId: string
  sessionId: string
  onBack: () => void
}

export function SessionDetailView({ accountId, projectId, sessionId, onBack }: SessionDetailViewProps) {
  const [view, setView] = useState<'transcript' | 'entities'>('transcript')

  const { data: sessionData, isLoading: loadingSession } = useQuery({
    queryKey: ['aiInterviewSession', accountId, projectId, sessionId],
    queryFn: () => aiInterviewApi.getSession(accountId, projectId, sessionId),
    staleTime: 60_000,
  })

  const { data: entities = [], isLoading: loadingEntities } = useQuery({
    queryKey: ['aiInterviewEntities', accountId, projectId, sessionId],
    queryFn: () => aiInterviewApi.getEntities(accountId, projectId, sessionId),
    staleTime: 60_000,
  })

  if (loadingSession || loadingEntities) {
    return (
      <div className="flex h-48 items-center justify-center text-fg-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-accent-500" /> Loading transcript...
      </div>
    )
  }

  const session = (sessionData as { session?: AIInterviewSession } | AIInterviewSession)
  const sess: AIInterviewSession = 'session' in (session ?? {})
    ? (session as { session: AIInterviewSession }).session
    : session as AIInterviewSession
  const messages: AIInterviewMessage[] =
    ((sessionData as { messages?: AIInterviewMessage[] })?.messages) ?? []
  const ents = entities as LiveExtractedEntity[]
  const conversationMessages = messages.filter((m) => m.role !== 'system')

  const duration = sess?.completedAt && sess?.createdAt
    ? Math.round((new Date(sess.completedAt).getTime() - new Date(sess.createdAt).getTime()) / 60000)
    : 0

  return (
    <div className="flex h-full flex-col bg-bg">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border bg-bg-card px-4 py-3">
        <button onClick={onBack} className="rounded-md p-1 text-fg-muted hover:bg-bg-secondary hover:text-fg" aria-label="Back to responses">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="truncate text-body font-medium text-fg">
            {sess?.intervieweeName || sess?.linkedPersonName || 'Anonymous'}
          </div>
          <div className="text-caption text-fg-muted">
            {sess?.createdAt ? formatDate(sess.createdAt) : ''}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-caption text-fg-secondary">
          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {conversationMessages.length}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {duration}m</span>
          <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> {ents.length}</span>
        </div>

        {/* Toggle */}
        <div className="ml-2 flex rounded-md border border-border bg-bg-secondary p-0.5">
          <button
            onClick={() => setView('transcript')}
            className={cn(
              'rounded px-3 py-1 text-caption transition-colors',
              view === 'transcript' ? 'bg-bg-card text-fg' : 'text-fg-muted hover:text-fg',
            )}
          >
            <FileText className="w-3 h-3 inline mr-1" /> Transcript
          </button>
          <button
            onClick={() => setView('entities')}
            className={cn(
              'rounded px-3 py-1 text-caption transition-colors',
              view === 'entities' ? 'bg-bg-card text-fg' : 'text-fg-muted hover:text-fg',
            )}
          >
            <Layers className="w-3 h-3 inline mr-1" /> Entities ({ents.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {view === 'transcript' ? (
          <TranscriptView messages={conversationMessages} personName={sess?.intervieweeName || sess?.linkedPersonName} />
        ) : (
          <EntityView entities={ents} />
        )}
      </div>
    </div>
  )
}

// ============================================================================
// TRANSCRIPT VIEW
// ============================================================================

function TranscriptView({ messages, personName }: { messages: AIInterviewMessage[]; personName?: string | null }) {
  if (messages.length === 0) {
    return <p className="py-8 text-center text-body text-fg-muted">No messages</p>
  }

  return (
    <div className="mx-auto max-w-2xl space-y-3">
      {messages.map((msg) => (
        <div key={msg.id} className={cn('flex gap-2', msg.role === 'user' ? 'flex-row-reverse' : '')}>
          <div className={cn(
            'max-w-[80%] rounded-md px-3 py-2 text-body',
            msg.role === 'ai'
              ? 'border border-border bg-bg-card text-fg'
              : 'bg-primary-500 text-bg-card',
          )}>
            <div className="mb-1 flex items-center gap-1.5">
              <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                {msg.role === 'ai' ? 'AI' : personName || 'User'}
              </Badge>
              {msg.inputMethod && msg.inputMethod !== 'text' && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0">{msg.inputMethod}</Badge>
              )}
            </div>
            <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// ENTITY VIEW
// ============================================================================

function EntityView({ entities }: { entities: LiveExtractedEntity[] }) {
  const byDomain = {
    market: entities.filter((e) => e.domain === 'market'),
    business: entities.filter((e) => e.domain === 'business'),
    solution: entities.filter((e) => e.domain === 'solution'),
  }

  if (entities.length === 0) {
    return <p className="py-8 text-center text-body text-fg-muted">No entities extracted</p>
  }

  return (
    <div className="mx-auto grid max-w-3xl grid-cols-3 gap-4 max-md:grid-cols-1">
      {(['market', 'business', 'solution'] as const).map((domain) => {
        const domainEntities = byDomain[domain]
        const colors = DOMAIN_COLORS[domain]
        return (
          <Card key={domain} className="rounded-md border border-border bg-bg-card p-3 shadow-none">
            <h3 className={cn('text-xs font-medium mb-2 capitalize', colors?.text)}>
              {domain} ({domainEntities.length})
            </h3>
            {domainEntities.length === 0 ? (
              <p className="text-[11px] text-fg-muted">No entities</p>
            ) : (
              <div className="space-y-1.5">
                {domainEntities.map((e, i) => (
                  <div key={`${e.type}-${e.name}-${i}`} className={cn('px-2 py-1.5 rounded text-[11px]', colors?.bg)}>
                    <div className="font-medium">{e.name}</div>
                    <div className={cn('text-[10px] mt-0.5', colors?.text)}>{e.type}</div>
                    {e.evidence && (
                      <div className="mt-1 line-clamp-2 text-[10px] italic text-fg-muted">&ldquo;{e.evidence}&rdquo;</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}
